import { useCallback } from "react";
import { useRef } from "react";
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useLocation, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { useInView } from "react-intersection-observer";

import HeaderSection from "./HeaderSection";
import TitleSection from "./TitleSection";
import CommentCard from "./CommentCard";
import CommentInput from "./CommentInput";
import EmptyComment from "./EmptyComment";
import UseWindowDimensions from "../../../utils/windowSize";
import {
  getSettingState,
  setCmState,
  setLoadNavigation,
  setReplyComment,
} from "../../../redux/features/settingSlice";
import { ChunkUser } from "../../../utils/ChunkWhile";
import LoadingCommentCard from "./LoadingCommentCard";
import CustomHelmet from "./../CustomHelmet/index";
import Player from "../Player";
import { fetcherGetToken } from "../../../services/fetcher";

const CommentPage = () => {
  const [comment, setComment] = useState("");
  const theme = useTheme();
  const { replyComment, cmState } = useSelector(getSettingState);
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  let urlParams = location.pathname.split("/");
  const { height } = UseWindowDimensions();
  const [endPage, setEndPage] = useState(false);
  const ref = useRef(null);
  const tokenStorage = JSON.parse(localStorage.getItem("token")) || null;

  useEffect(() => {
    dispatch(setReplyComment(null));
  }, [dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const conditionalReq = useCallback(() => {
    let topic = null;
    let cm = null;
    let backUrl = null;

    if (urlParams[3] === "opinions") {
      topic = `/content/topic/${params.topicId}/`;
      cm = `/content/comments/topic/${params.topicId}/`;
      backUrl = `/table/${params.tableId}/opinions/${params.opinionId}/topics/${params.topicId}`;
    }
    if (urlParams[3] === "discussions") {
      topic = `/content/converse/${params.topicId}/`;
      cm = `/content/comments/converse/${params.topicId}/`;
      backUrl = `/table/${params.tableId}/discussions/${params.discussionId}/topics/${params.topicId}`;
    }
    if (urlParams[3] === "comment") {
      topic = `/content/table/${params.tableId}/`;
      cm = `/content/table-comments/${params.tableId}/`;
      backUrl = `/table/${params.tableId}/}`;
    }

    return { topic, cm, backUrl };
  });

  const { ref: refTop, inView: vTop } = useInView({
    threshold: 0,
  });

  const { data: topicData } = useSWR(() => {
    // if (urlParams[3] === "comment") {
    //   return null;
    // }
    return [process.env.REACT_APP_API + conditionalReq().topic, tokenStorage];
  }, fetcherGetToken);

  const {
    data: comments,
    isLoading,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData.results.length) return null;

      return (
        process.env.REACT_APP_API +
        conditionalReq().cm +
        `?page=${pageIndex + 1}&page_size=30`
      );
    },
    {
      revalidateFirstPage: true,
      initialSize: 1,
      revalidateAll: false,
    }
  );

  // Refresh Comment
  const useComments = () => {
    const { mutate } = useSWRInfinite(() => {
      return (
        process.env.REACT_APP_API +
        conditionalReq().cm +
        `?page=${1}&page_size=30`
      );
    });
    return {
      refresh: mutate,
    };
  };

  const { refresh } = useComments();
  useEffect(() => {
    const interval = setInterval(refresh, 6000);

    return () => clearInterval(interval);
  }, [conditionalReq, refresh]);

  useEffect(() => {
    !isLoading && dispatch(setLoadNavigation(isValidating));
  }, [dispatch, isLoading, isValidating]);

  useEffect(() => {
    vTop && moreInfinite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vTop]);

  const moreInfinite = () => {
    if (comments) {
      const moreTopicCart = size + 1 <= comments[0]?.total_pages;
      if (moreTopicCart) {
        setSize(size + 1);
        dispatch(setLoadNavigation(true));
      } else {
        setSize(comments[0].total_pages);
        setEndPage(true);
      }
    }
  };

  useEffect(() => {
    if (comments) {
      var cmList = [];
      // eslint-disable-next-line array-callback-return
      comments.map((e) => {
        cmList.push(...e.results);
      });

      if (cmState && isValidating) {
        // console.log(1);
        const filterCm = cmList.filter(
          (obj1) => !cmState.some((obj2) => obj1.pk === obj2.pk)
        );

        dispatch(
          setCmState([...filterCm, ...cmState].sort((a, b) => b.pk - a.pk))
        );
      } else {
        // console.log(2);
        dispatch(setCmState([...cmList].sort((a, b) => b.pk - a.pk)));
      }
    }

    return () => {
      dispatch(setCmState(null));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, comments, location]);

  return (
    <>
      {topicData && <CustomHelmet title={`${topicData.title} | Comment`} />}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          zIndex: 1,
          width: "100%",
        }}
      >
        <HeaderSection
          cmCount={
            urlParams[3] === "comment"
              ? topicData?.all_comments_count
              : topicData?.comments_count
          }
        />
        <Box sx={{ display: "none" }}>
          <Player ref={ref} />
        </Box>

        <Box
          sx={{
            width: "100%",
            height:
              height -
              (replyComment ? 168 : 130) -
              (comment.length > 20 ? 20 : 0) +
              "px",
            overflowY: "scroll",
            mt: "56px",
            backgroundColor: theme.palette.white.main,
            display: "flex",
            flexDirection: "column-reverse",
            pb: "10px",
          }}
        >
          {!isLoading &&
            comments &&
            cmState &&
            ChunkUser(cmState).map((rowData, index) => {
              return (
                <Box
                  key={index}
                  sx={{
                    my: "4px",
                    display: "flex",
                    flexDirection: "column-reverse",
                  }}
                >
                  {rowData.map((row, i, { length }) => {
                    return (
                      <CommentCard
                        key={row.pk}
                        cmData={row}
                        firstIndex={i === 0}
                        lastIndex={i + 1 === length}
                      />
                    );
                  })}
                </Box>
              );
            })}

          {isLoading && cmState && (
            <Box sx={{ textAlign: "center", mt: "-1px" }}>
              <LinearProgress />
            </Box>
          )}

          {(isLoading || !comments) && (
            <Box sx={{ textAlign: "center", mt: "5px" }}>
              <LoadingCommentCard />
            </Box>
          )}

          {!isLoading && comments && (
            <Box
              ref={refTop}
              sx={{ display: "flex", justifyContent: "center", py: "2px" }}
            ></Box>
          )}

          {!isLoading && comments && cmState?.length === 0 && <EmptyComment />}

          {((!isLoading && comments && cmState?.length === 0) || endPage) &&
            urlParams[3] !== "comment" && (
              <TitleSection
                data={topicData}
                topicUrl={conditionalReq().backUrl}
                cmLength={cmState?.length}
              />
            )}
          {urlParams[3] === "comment" &&
            cmState?.length > 0 &&
            endPage &&
            !isValidating && (
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#757575",
                  fontWeight: "600",
                  padding: "6px 0",
                  fontSize: "12px",
                  mt: "10px",
                  mb: "auto",
                }}
              >
                Conversation started
              </Typography>
            )}
          <CommentInput
            conUrl={conditionalReq()}
            pageType={urlParams[3]}
            comment={comment}
            setComment={setComment}
          />
        </Box>
      </Box>
    </>
  );
};

export default CommentPage;
